import { useMemo, useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import {
  useDealPromoGroups,
  useUpdateDealPromoGroup,
} from "src/dm/hooks/useDealPromoGroups";

const DealPromoGroupsPage = () => {
  const deal = useDealContext();
  const { data, isLoading, isError, error } = useDealPromoGroups(deal.dealId);
  const update = useUpdateDealPromoGroup(deal.dealId);

  // The legacy ViewPromoGroups.aspx edits a single deal-level value
  // (NBR_STRS_IN_WRAP). Our API surface keeps the per-promo-group PUT for
  // future per-group fields, but right now editing any row updates the same
  // deal-level cap — so we expose a single editable field above the grid
  // and persist via the first promo group's PUT route.
  const dealMax = data?.[0]?.maxStoreCountAllowedForWrap ?? null;
  const totalStores = useMemo(
    () => (data ?? []).reduce((acc, r) => acc + (r.storeCount ?? 0), 0),
    [data]
  );

  const [editing, setEditing] = useState(false);
  const [draftMax, setDraftMax] = useState<number | null>(dealMax);
  const [saveError, setSaveError] = useState<string | null>(null);

  if (isLoading) return <p>Loading promo groups…</p>;
  if (isError)
    return (
      <p style={{ color: "#b91c1c" }}>
        {error instanceof Error ? error.message : "Failed to load"}
      </p>
    );

  const onEdit = () => {
    setDraftMax(dealMax);
    setSaveError(null);
    setEditing(true);
  };
  const onCancel = () => {
    setEditing(false);
    setSaveError(null);
  };
  const onSave = async () => {
    if (draftMax == null || draftMax < 1 || draftMax > 99999) {
      setSaveError("Enter a value between 1 and 99,999.");
      return;
    }
    if (draftMax < totalStores) {
      setSaveError(
        `Max stores (${draftMax}) must be at least the total of all stores in the promotion groups (${totalStores}).`
      );
      return;
    }
    const target = data?.[0]?.promoGroupId;
    if (target === undefined) {
      setSaveError("No promo groups available to update.");
      return;
    }
    try {
      await update.mutateAsync({
        promoGroupId: target,
        request: { maxStoreCountAllowedForWrap: draftMax },
      });
      setEditing(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Save failed");
    }
  };

  const canEdit = (data?.length ?? 0) > 0;

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Promo Groups</h2>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          margin: "0.75rem 0 1rem",
        }}
      >
        <label style={{ fontWeight: 600 }}>Max Number of Stores:</label>
        {editing ? (
          <>
            <NumericTextBox
              value={draftMax ?? undefined}
              onChange={(e) =>
                setDraftMax(typeof e.value === "number" ? e.value : null)
              }
              min={1}
              max={99999}
              format="n0"
              width={120}
            />
            <Button
              type="button"
              size="small"
              themeColor="primary"
              onClick={onSave}
              disabled={update.isPending}
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            <Button type="button" size="small" onClick={onCancel}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <span style={{ minWidth: 60, textAlign: "right" }}>
              {dealMax ?? "—"}
            </span>
            {canEdit && (
              <Button type="button" size="small" onClick={onEdit}>
                Edit
              </Button>
            )}
          </>
        )}
        <span style={{ color: "#64748b", marginLeft: "1rem" }}>
          Total stores across groups: <strong>{totalStores}</strong>
        </span>
      </div>
      {saveError && (
        <p style={{ color: "#b91c1c", marginTop: 0 }}>{saveError}</p>
      )}

      {(data?.length ?? 0) === 0 ? (
        <p style={{ color: "#94a3b8" }}>
          No promo groups are referenced by this deal.
        </p>
      ) : (
        <Grid data={data}>
          <GridColumn field="promoGroupId" title="ID" width="100px" />
          <GridColumn
            field="promoGroupDescription"
            title="Promo Group Description"
          />
          <GridColumn field="storeCount" title="Total Stores" width="140px" />
        </Grid>
      )}
    </div>
  );
};

export default DealPromoGroupsPage;
